import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/adminlayout';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';
import { PRODUCTION_URL } from '../productionLink/productionLink';

const FrontOfficeSetup = () => {
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [activeTab, setActiveTab] = useState('rates'); // rates, settings
  const [rates, setRates] = useState([]);
  const [settings, setSettings] = useState([]);
  const [selectedRate, setSelectedRate] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editData, setEditData] = useState({});
  const [successMessage, setSuccessMessage] = useState('');

  const colors = {
    primary: '#2D5016',
    accent: '#6ba83e',
    lightGray: '#f8faf9',
    mediumGray: '#e8ede9',
    textDark: '#1a1a1a',
    textLight: '#6b7280',
    white: '#ffffff',
    success: '#4a7c2a',
    warning: '#f59e0b',
    danger: '#ef4444',
    info: '#3b82f6',
    purple: '#8b5cf6'
  };

  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 4000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const fetchData = async () => {
    setLoading(true);
    await Promise.all([fetchRates(), fetchSettings()]);
    setLoading(false);
  };

  const fetchRates = async () => {
    try {
      const data = await apiGet('/admin/front-office/setup/rates');
      if (data.success) setRates(data.data);
    } catch (error) {
      console.error('Error fetching rates:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const data = await apiGet('/admin/front-office/settings');
      if (data.success) setSettings(data.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleEditRate = (rate) => {
    setSelectedRate(rate);
    setEditData({
      base_rate: rate.base_rate,
      weekend_rate: rate.weekend_rate || rate.base_rate,
      effective_from: rate.effective_from?.split('T')[0] || '',
      effective_to: rate.effective_to?.split('T')[0] || ''
    });
    setShowEditModal(true);
  };

  const handleAddRate = () => {
    setEditData({
      room_type: '',
      season: 'regular',
      base_rate: '',
      weekend_rate: '',
      effective_from: new Date().toISOString().split('T')[0],
      effective_to: ''
    });
    setShowAddModal(true);
  };

  const handleSaveRate = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${PRODUCTION_URL}/api/admin/front-office/setup/rates/${selectedRate.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editData)
      });
      const data = await response.json();
      if (data.success) {
        setSuccessMessage('✅ Rate updated successfully!');
        setShowEditModal(false);
        fetchRates();
      }
    } catch (error) {
      console.error('Error updating rate:', error);
      setSuccessMessage('❌ Error updating rate');
    }
  };

  const handleUpdateSetting = async (settingKey, value) => {
    try {
      const response = await fetch(`${PRODUCTION_URL}/api/admin/front-office/settings/${settingKey}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value })
      });
      const data = await response.json();
      if (data.success) {
        setSuccessMessage('✅ Setting updated successfully!');
        fetchSettings();
      }
    } catch (error) {
      console.error('Error updating setting:', error);
      setSuccessMessage('❌ Error updating setting');
    }
  };

  const styles = {
    pageHeader: {
      marginBottom: isMobile ? '20px' : '28px',
      padding: isMobile ? '0 8px' : '0'
    },
    pageTitle: {
      fontSize: isMobile ? '22px' : isTablet ? '26px' : '32px',
      fontWeight: '800',
      color: colors.textDark,
      marginBottom: '8px',
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 100%)`,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent'
    },
    pageSubtitle: {
      fontSize: isMobile ? '13px' : '15px',
      color: colors.textLight,
      fontWeight: '500'
    },
    tabContainer: {
      display: 'flex',
      gap: '8px',
      marginBottom: isMobile ? '20px' : '24px',
      padding: isMobile ? '0 8px' : '0',
      flexWrap: 'wrap'
    },
    tab: {
      padding: isMobile ? '10px 16px' : '12px 24px',
      fontSize: isMobile ? '13px' : '15px',
      fontWeight: '600',
      cursor: 'pointer',
      border: 'none',
      background: 'transparent',
      color: colors.textLight,
      borderRadius: '8px',
      transition: 'all 0.3s ease'
    },
    activeTab: {
      color: colors.primary,
      background: `${colors.primary}15`,
      fontWeight: '700'
    },
    contentCard: {
      background: colors.white,
      padding: isMobile ? '18px' : '24px',
      borderRadius: isMobile ? '14px' : '18px',
      boxShadow: '0 8px 24px rgba(45, 80, 22, 0.1)',
      border: `1px solid ${colors.mediumGray}`,
      margin: isMobile ? '0 8px 16px 8px' : '0 0 24px 0'
    },
    sectionTitle: {
      fontSize: isMobile ? '18px' : '22px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: isMobile ? '16px' : '20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '10px'
    },
    rateGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
      gap: isMobile ? '14px' : '20px'
    },
    rateCard: {
      padding: isMobile ? '18px' : '22px',
      border: `2px solid ${colors.mediumGray}`,
      borderRadius: '14px',
      background: colors.lightGray,
      transition: 'all 0.3s ease',
      cursor: 'pointer'
    },
    button: {
      padding: isMobile ? '8px 16px' : '10px 20px',
      borderRadius: '8px',
      border: 'none',
      fontSize: isMobile ? '12px' : '14px',
      fontWeight: '700',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      textTransform: 'uppercase',
      letterSpacing: '0.3px'
    },
    primaryButton: {
      background: colors.primary,
      color: colors.white,
      boxShadow: '0 2px 8px rgba(45, 80, 22, 0.3)'
    },
    successButton: {
      background: colors.success,
      color: colors.white,
      boxShadow: '0 2px 8px rgba(74, 124, 42, 0.3)'
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '20px'
    },
    modalContent: {
      background: colors.white,
      borderRadius: '20px',
      padding: isMobile ? '24px' : '32px',
      maxWidth: '600px',
      width: '100%',
      maxHeight: '90vh',
      overflowY: 'auto',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
    },
    formGroup: {
      marginBottom: '18px'
    },
    label: {
      display: 'block',
      marginBottom: '8px',
      fontSize: '13px',
      fontWeight: '700',
      color: colors.textDark,
      textTransform: 'uppercase',
      letterSpacing: '0.3px'
    },
    input: {
      width: '100%',
      padding: '12px 14px',
      borderRadius: '8px',
      border: `1.5px solid ${colors.mediumGray}`,
      fontSize: '14px',
      fontFamily: 'inherit',
      transition: 'all 0.3s ease',
      background: colors.white,
      color: colors.textDark,
      boxSizing: 'border-box',
      position: 'relative',
      zIndex: 1
    },
    select: {
      width: '100%',
      padding: '12px 14px',
      borderRadius: '8px',
      border: `1.5px solid ${colors.mediumGray}`,
      fontSize: '14px',
      fontFamily: 'inherit',
      cursor: 'pointer',
      background: colors.white,
      color: colors.textDark,
      boxSizing: 'border-box',
      position: 'relative',
      zIndex: 1
    },
    successAlert: {
      position: 'fixed',
      top: '20px',
      right: '20px',
      background: successMessage.includes('❌') ? colors.danger : colors.success,
      color: colors.white,
      padding: '16px 24px',
      borderRadius: '12px',
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
      fontSize: '14px',
      fontWeight: '600',
      zIndex: 10000,
      animation: 'slideIn 0.3s ease'
    },
    settingItem: {
      padding: isMobile ? '16px' : '20px',
      background: colors.lightGray,
      borderRadius: '12px',
      marginBottom: '16px',
      border: `1px solid ${colors.mediumGray}`
    },
    badge: {
      padding: '6px 12px',
      borderRadius: '6px',
      fontSize: '11px',
      fontWeight: '700',
      display: 'inline-block',
      textTransform: 'uppercase',
      letterSpacing: '0.3px'
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(255, 255, 255, 0.9)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9998
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <div style={{ fontSize: '18px', fontWeight: '600', color: colors.primary }}>
              Loading Setup...
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {successMessage && (
        <div style={styles.successAlert}>
          {successMessage}
        </div>
      )}

      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>⚙️ Front Office Setup</h1>
        <p style={styles.pageSubtitle}>
          Configure rates, settings, and system parameters
        </p>
      </div>

      {/* Tabs */}
      <div style={styles.tabContainer}>
        <button
          onClick={() => setActiveTab('rates')}
          style={{
            ...styles.tab,
            ...(activeTab === 'rates' ? styles.activeTab : {})
          }}
        >
          💰 Room Rates
        </button>
        <button
          onClick={() => setActiveTab('settings')}
          style={{
            ...styles.tab,
            ...(activeTab === 'settings' ? styles.activeTab : {})
          }}
        >
          ⚙️ System Settings
        </button>

      </div>

      {/* Room Rates Tab */}
      {activeTab === 'rates' && (
        <div style={styles.contentCard}>
          <div style={styles.sectionTitle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span>💰</span>
              <span>Room Rates Configuration</span>
            </div>
          </div>

          {rates.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: isMobile ? '40px 20px' : '60px 40px',
              color: colors.textLight
            }}>
              <div style={{ fontSize: isMobile ? '48px' : '64px', marginBottom: '16px', opacity: 0.5 }}>
                💰
              </div>
              <div style={{ fontSize: isMobile ? '14px' : '16px' }}>
                No room rates configured yet
              </div>
            </div>
          ) : (
            <div style={styles.rateGrid}>
              {rates.map(rate => (
                <div
                  key={rate.id}
                  style={styles.rateCard}
                  onClick={() => handleEditRate(rate)}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 20px rgba(45, 80, 22, 0.15)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{
                    fontSize: isMobile ? '18px' : '20px',
                    fontWeight: '800',
                    marginBottom: '12px',
                    color: colors.primary
                  }}>
                    {rate.room_type}
                  </div>

                  <div style={{ marginBottom: '12px' }}>
                    <span style={{
                      ...styles.badge,
                      background: rate.season === 'peak'
                        ? 'rgba(239, 68, 68, 0.15)'
                        : rate.season === 'off_season'
                          ? 'rgba(107, 168, 62, 0.15)'
                          : 'rgba(59, 130, 246, 0.15)',
                      color: rate.season === 'peak'
                        ? colors.danger
                        : rate.season === 'off_season'
                          ? colors.accent
                          : colors.info
                    }}>
                      {rate.season || 'regular'}
                    </span>
                  </div>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '12px',
                    marginBottom: '16px'
                  }}>
                    <div style={{
                      background: colors.white,
                      padding: '12px',
                      borderRadius: '8px'
                    }}>
                      <div style={{ fontSize: '10px', color: colors.textLight, marginBottom: '4px', fontWeight: '600' }}>
                        Base Rate
                      </div>
                      <div style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: '800', color: colors.primary }}>
                        ₹{parseFloat(rate.base_rate).toLocaleString('en-IN')}
                      </div>
                    </div>

                    <div style={{
                      background: colors.white,
                      padding: '12px',
                      borderRadius: '8px'
                    }}>
                      <div style={{ fontSize: '10px', color: colors.textLight, marginBottom: '4px', fontWeight: '600' }}>
                        Weekend
                      </div>
                      <div style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: '800', color: colors.accent }}>
                        ₹{parseFloat(rate.weekend_rate || rate.base_rate).toLocaleString('en-IN')}
                      </div>
                    </div>
                  </div>

                  <div style={{
                    fontSize: '11px',
                    color: colors.textLight,
                    marginBottom: '12px',
                    padding: '8px',
                    background: colors.white,
                    borderRadius: '6px'
                  }}>
                    📅 Effective from: {new Date(rate.effective_from).toLocaleDateString()}
                    {rate.effective_to && ` - ${new Date(rate.effective_to).toLocaleDateString()}`}
                  </div>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditRate(rate);
                    }}
                    style={{ ...styles.button, ...styles.primaryButton, width: '100%' }}
                  >
                    Edit Rate
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* System Settings Tab */}
      {activeTab === 'settings' && (
        <div style={styles.contentCard}>
          <div style={styles.sectionTitle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span>⚙️</span>
              <span>System Settings</span>
            </div>
          </div>

          {settings.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: isMobile ? '40px 20px' : '60px 40px',
              color: colors.textLight
            }}>
              <div style={{ fontSize: isMobile ? '48px' : '64px', marginBottom: '16px', opacity: 0.5 }}>
                ⚙️
              </div>
              <div style={{ fontSize: isMobile ? '14px' : '16px' }}>
                No system settings available
              </div>
            </div>
          ) : (
            <div>
              {settings.map((setting, index) => (
                <div key={index} style={styles.settingItem}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                    gap: '12px'
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: isMobile ? '14px' : '16px',
                        fontWeight: '700',
                        marginBottom: '6px',
                        color: colors.textDark
                      }}>
                        {setting.setting_key?.replace(/_/g, ' ').toUpperCase()}
                      </div>
                      <div style={{ fontSize: '13px', color: colors.textLight }}>
                        {setting.description || 'System configuration setting'}
                      </div>
                    </div>
                    <div style={{
                      fontSize: isMobile ? '14px' : '16px',
                      fontWeight: '700',
                      color: colors.primary,
                      padding: '8px 16px',
                      background: colors.white,
                      borderRadius: '8px'
                    }}>
                      {setting.setting_value}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Quick Settings Options */}
          <div style={{ marginTop: '24px' }}>
            <h3 style={{
              fontSize: isMobile ? '16px' : '18px',
              fontWeight: '700',
              marginBottom: '16px',
              color: colors.textDark
            }}>
              Quick Configuration
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
              gap: '16px'
            }}>
              <div style={{
                padding: '20px',
                background: colors.lightGray,
                borderRadius: '12px',
                border: `2px solid ${colors.mediumGray}`
              }}>
                <div style={{ fontSize: '14px', fontWeight: '700', marginBottom: '8px', color: colors.textDark }}>
                  Check-in Time
                </div>
                <input
                  type="time"
                  defaultValue="14:00"
                  style={styles.input}
                  onChange={(e) => handleUpdateSetting('check_in_time', e.target.value)}
                />
              </div>

              <div style={{
                padding: '20px',
                background: colors.lightGray,
                borderRadius: '12px',
                border: `2px solid ${colors.mediumGray}`
              }}>
                <div style={{ fontSize: '14px', fontWeight: '700', marginBottom: '8px', color: colors.textDark }}>
                  Check-out Time
                </div>
                <input
                  type="time"
                  defaultValue="11:00"
                  style={styles.input}
                  onChange={(e) => handleUpdateSetting('check_out_time', e.target.value)}
                />
              </div>

              <div style={{
                padding: '20px',
                background: colors.lightGray,
                borderRadius: '12px',
                border: `2px solid ${colors.mediumGray}`
              }}>
                <div style={{ fontSize: '14px', fontWeight: '700', marginBottom: '8px', color: colors.textDark }}>
                  Tax Rate (%)
                </div>
                <input
                  type="number"
                  step="0.01"
                  defaultValue="18"
                  style={styles.input}
                  onChange={(e) => handleUpdateSetting('tax_rate', e.target.value)}
                />
              </div>

              <div style={{
                padding: '20px',
                background: colors.lightGray,
                borderRadius: '12px',
                border: `2px solid ${colors.mediumGray}`
              }}>
                <div style={{ fontSize: '14px', fontWeight: '700', marginBottom: '8px', color: colors.textDark }}>
                  Currency
                </div>
                <select
                  defaultValue="INR"
                  style={styles.select}
                  onChange={(e) => handleUpdateSetting('currency', e.target.value)}
                >
                  <option value="INR">INR (₹)</option>
                  <option value="USD">USD ($)</option>
                  <option value="EUR">EUR (€)</option>
                  <option value="GBP">GBP (£)</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}



      {/* Edit Rate Modal */}
      {showEditModal && selectedRate && (
        <div style={styles.modal} onClick={() => setShowEditModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={{
              fontSize: isMobile ? '20px' : '24px',
              marginBottom: '24px',
              fontWeight: '800',
              color: colors.primary
            }}>
              Edit Rate - {selectedRate.room_type}
            </h3>

            <form onSubmit={handleSaveRate}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Season</label>
                <div style={{
                  padding: '12px 14px',
                  background: colors.lightGray,
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: colors.textDark
                }}>
                  {selectedRate.season || 'Regular'}
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Base Rate (₹) *</label>
                <input
                  type="number"
                  step="0.01"
                  value={editData.base_rate}
                  onChange={(e) => setEditData({ ...editData, base_rate: e.target.value })}
                  style={styles.input}
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Weekend Rate (₹)</label>
                <input
                  type="number"
                  step="0.01"
                  value={editData.weekend_rate}
                  onChange={(e) => setEditData({ ...editData, weekend_rate: e.target.value })}
                  style={styles.input}
                />
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                gap: '16px'
              }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Effective From *</label>
                  <input
                    type="date"
                    value={editData.effective_from}
                    onChange={(e) => setEditData({ ...editData, effective_from: e.target.value })}
                    style={styles.input}
                    required
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Effective To</label>
                  <input
                    type="date"
                    value={editData.effective_to}
                    onChange={(e) => setEditData({ ...editData, effective_to: e.target.value })}
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button
                  type="submit"
                  style={{ ...styles.button, ...styles.successButton, flex: 1, padding: '14px' }}
                >
                  Save Changes
                </button>
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  style={{
                    ...styles.button,
                    background: colors.mediumGray,
                    color: colors.textDark,
                    flex: 1,
                    padding: '14px'
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default FrontOfficeSetup;